import React, { useContext, useMemo, useCallback, useEffect, useRef, forwardRef, SyntheticEvent } from 'react';
import moment from 'moment';
import { FetchPolicy } from '@apollo/client';
import { Col, Event, factory, EVENTS_QUERY, useAuthenticatedQuery } from '@vodafoneis/sjonvarpskjarni-js-lib';
import Carousel from '../../../Carousel';
import { CarouselContext, CarouselContextProvider } from '../../../../contexts/CarouselContext';
import ChannelPlaylistItem from '../../../../models/ChannelPlaylistItem';
import { PlaybackInformationContext } from '../../../../contexts/PlaybackInformationContext';
import { PlaybackContext } from '../../../../contexts/PlaybackContext';
import { track } from '../../../../utils/Analytics';
import { AnalyticsEvent } from '../../../../utils/AnalyticsEvent';
import { isForChildren } from '../../../../utils/eventUtils';

type ProgramsListEventProps = {
	event: Event;
};

const ProgramsListEvent = forwardRef<any, ProgramsListEventProps>(({ event }, ref) => {
	const { TIMESHIFT_START } = AnalyticsEvent;
	const { player, position, duration, date } = useContext(PlaybackInformationContext);
	const { clickEnabled } = useContext(CarouselContext);

	const currentlyPlayingTimestamp = useMemo<number>(() => date.getTime(), [date]);

	const startTimestamp = useMemo(() => currentlyPlayingTimestamp - position * 1000, [currentlyPlayingTimestamp, position]);
	const endTimestamp = useMemo(() => currentlyPlayingTimestamp + (duration - position) * 1000, [currentlyPlayingTimestamp, duration, position]);

	const canPlay = useMemo(() => event.startTime.getTime() < endTimestamp, [endTimestamp, event]);
	const isLive = useMemo(() => canPlay && event.endTime.getTime() > endTimestamp, [canPlay, endTimestamp, event]);
	const isCurrentlyPlaying = useMemo(() => event.startTime.getTime() < currentlyPlayingTimestamp && event.endTime.getTime() > currentlyPlayingTimestamp, [currentlyPlayingTimestamp, event]);
	const { playlistItem, prevPlaylistItem, setPlaybackEventState } = useContext(PlaybackContext);

	const onEventSelected = useCallback(
		(e: SyntheticEvent) => {
			e.preventDefault();

			if (!clickEnabled) return;

			if (event.startTime.getTime() > startTimestamp && event.startTime.getTime() < endTimestamp) {
				setPlaybackEventState(event);

				const startPosition = (event.startTime.getTime() - startTimestamp) / 1000;
				const stopPosition = Math.round(prevPlaylistItem.lastKnownPosition);
				player.seekToPosition(startPosition);

				const item = playlistItem as ChannelPlaylistItem;

				track(TIMESHIFT_START, {
					episodeNumber: event.episodeNumber,
					episodeSeason: event.seasonNumber,
					itemId: item.channel.operatorRef,
					itemTitle: item.channel.title,
					playbackSessionId: playlistItem.playbackSessionId,
					programDuration: event.getDuration(),
					itemChildren: isForChildren(event.genre),
					programId: event.id,
					programStart: event.startTime.getTime(),
					programTitle: event.title,
					startPosition: Math.round(startPosition),
					stopPosition,
				});
			}
		},
		[clickEnabled, event, startTimestamp, endTimestamp, setPlaybackEventState, prevPlaylistItem.lastKnownPosition, player, playlistItem, TIMESHIFT_START]
	);

	if (event.startTime.getTime() < startTimestamp) return null;

	return (
		<Col ref={ref} xl={3} md={6} xs={12}>
			<div role={'button'} onClick={onEventSelected} className={`event carousel-item${canPlay ? ' playable' : ''}${isLive ? ' live' : ''}${isCurrentlyPlaying ? ' current' : ''}`}>
				<div className={'event-play-icon'} />
				<div className={'event-info-wrapper'}>
					<div className={'event-title'}>{event.title}</div>
					<div className={'event-subtitle'}>{moment(event.startTime).calendar()}</div>
				</div>
				<div className={'event-live-dot'} />
			</div>
		</Col>
	);
});

const ProgramsListContent: React.FC = () => {
	const { previousPage, nextPage, leftArrowVisible, rightArrowVisible, scroll } = useContext(CarouselContext);
	const { position, date } = useContext(PlaybackInformationContext);
	const { playlistItem } = useContext(PlaybackContext);

	const startHour = useMemo(() => {
		const now = new Date();
		const fetchFrom = new Date();
		fetchFrom.setHours(now.getHours() - 6);
		return Math.round(fetchFrom.getTime() / 3600000);
	}, []);

	const queryOptions = useMemo(
		() => ({
			fetchPolicy: 'no-cache' as FetchPolicy,
			variables: {
				stationId: (playlistItem as ChannelPlaylistItem).channel.epgStationId,
				startHour,
				deltaHours: 18,
			},
		}),
		[playlistItem, startHour]
	);

	const { data } = useAuthenticatedQuery(EVENTS_QUERY, queryOptions);

	const events = useMemo(() => {
		if (!data || !data.events) return null;

		return data.events.map((event: SyntheticEvent) => factory(event));
	}, [data]);

	const eventComponentPositions = useRef({});

	const eventComponents = useMemo(() => {
		if (!events) return null;

		return events.map((event: Event, index: number) => (
			<ProgramsListEvent
				ref={(ref) => {
					if (ref) {
						eventComponentPositions.current[index] = ref.offsetLeft;
					}
				}}
				// eslint-disable-next-line react/no-array-index-key
				key={String(index)}
				event={event}
			/>
		));
	}, [events]);

	const indexOfCurrentEvent = useMemo(() => {
		if (!date || !events) return -1;

		const currentlyPlayingTimestamp = date.getTime();
		const startTimestamp = currentlyPlayingTimestamp - position * 1000;

		return events.findIndex((event) => event.startTime.getTime() > startTimestamp && event.startTime.getTime() < currentlyPlayingTimestamp && event.endTime.getTime() > currentlyPlayingTimestamp);
	}, [date, events, position]);

	useEffect(() => {
		const scrollPosition = eventComponentPositions.current[indexOfCurrentEvent];

		if (scrollPosition) {
			scroll((scrollPosition + 5) * -1);
		}
	}, [indexOfCurrentEvent, scroll]);

	const onPreviousClick = useCallback(
		(e: SyntheticEvent) => {
			e.preventDefault();
			previousPage();
		},
		[previousPage]
	);

	const onNextClick = useCallback(
		(e: SyntheticEvent) => {
			e.preventDefault();
			nextPage();
		},
		[nextPage]
	);

	if (!events) return null;

	return (
		<>
			<Carousel>
				<div className={'row'}>{eventComponents}</div>
			</Carousel>
			<div className={'events-actions'}>
				<div role={'button'} className={`previous${leftArrowVisible ? ' visible' : ''}`} onClick={onPreviousClick} />
				<div role={'button'} className={`next${rightArrowVisible ? ' visible' : ''}`} onClick={onNextClick} />
			</div>
		</>
	);
};

const preventPropagation = (e: SyntheticEvent) => {
	e.stopPropagation();
};

export const ProgramsList: React.FC = () => {
	const { player, date } = useContext(PlaybackInformationContext);
	const { playlistItem } = useContext(PlaybackContext);

	if (!(playlistItem instanceof ChannelPlaylistItem) || !date || !player) return null;

	return (
		<div className={'event-list'} onTouchStart={preventPropagation} onTouchEnd={preventPropagation} onDoubleClick={preventPropagation} onClick={preventPropagation}>
			<CarouselContextProvider>
				<ProgramsListContent />
			</CarouselContextProvider>
		</div>
	);
};
